import React, { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import { DndProvider, useDrop } from 'react-dnd';
import HTML5Backend, { NativeTypes } from 'react-dnd-html5-backend';
import { getFilesFromDragEvent } from 'html-dir-content';
import { UPLOAD_SOURCES } from '@cld/upload-widget-common';
import t from 'common/i18n';
import { isMobile } from 'utils/domUtils';
import { filterJunkFiles, getFilePathProps } from 'utils/fileUtils';
import TabPanel from 'tabs/hocs/TabPanel';
import { selectDictionaryTextVersion, selectClientAllowedFormatsOptions } from 'root/selectors';
import TabFooter from 'root/components/TabFooter';
import SvgIcon from 'root/components/SvgIcon';
import { getComponentConsumer } from 'root/components/UploadContainer/uploaderContext';
import UploadButton from './components/UploadButton';
import SVG_ICONS from '~/assets/icons/icons';

import './LocalTab.scss';

const accept = [NativeTypes.FILE, NativeTypes.URL];

const FolderAwareHTML5Backend = (manager) => {
  const backend = HTML5Backend(manager);
  const handleTopDropCapture = backend.handleTopDropCapture;

  backend.handleTopDropCapture = (e) => {
    handleTopDropCapture.call(backend, e);
    backend.currentNativeSource.item.dirContent = getFilesFromDragEvent(e, { recursive: true, withFullPath: true });
  };

  return backend;
};

const TabCenterContent = ({ isDragHover }) => {
  return (
    <>
      <div className="icon-upload mb4 tc dn db-ns">
        <SvgIcon size={100} path={SVG_ICONS.UPLOAD} colorName={isDragHover ? 'action' : 'window-border'} />
      </div>

      <div className="mb4 tc db dn-ns">
        <SvgIcon size={125} path={SVG_ICONS.FILES_DRAG} colorName="window-border" />
      </div>

      <strong className="c-window-border title single-text">{t('local.dd_title_single')}</strong>
      <strong className="c-window-border title multi-text">{t('local.dd_title_multi')}</strong>
      <strong className="c-window-border mb4 title add single-text c-action">{t('local.drop_title_single')}</strong>
      <strong className="c-window-border mb4 title add multi-text c-action">{t('local.drop_title_multiple')}</strong>

      <span className="or">{t('or')}</span>
    </>
  );
};

const LocalTab = ({ addToUploadQueue, uploader }) => {
  //re-render local tab on text changes
  useSelector(selectDictionaryTextVersion);
  const uploadAllowedFormat = useSelector(selectClientAllowedFormatsOptions);

  const buttonContainerRef = useRef(null);

  const [{ isDragHover }, ddAreaRef] = useDrop({
    accept,
    async drop(item) {
      if (item.urls?.length) {
        addToUploadQueue({ files: item.urls });
      } else {
        const dirContent = await item.dirContent;
        const files = filterJunkFiles(dirContent).map((file) => {
          const { folder } = getFilePathProps(file.hdcFullPath || file.name);
          return Object.assign(file, { folder });
        });
        if (files) {
          addToUploadQueue({ files });
        }
      }
      return {
        dndFinished: true,
      };
    },
    collect: (monitor) => ({
      isDragHover: !!monitor.isOver(),
    }),
  });

  useEffect(() => {
    //render the upload field only once on mount
    uploader.renderUploadField(buttonContainerRef.current, ddAreaRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="panel-inner-container">
      <div
        data-test="drag-area"
        // eslint-disable-next-line react/no-unknown-property
        styles={{ border: '10px solid pink' }}
        className={cx('bg-window', 'drag_area content-wrap bg-source-bg bg-source-bg-c', 'browse-box', {
          'drag-active': isDragHover,
        })}
        ref={ddAreaRef}
      >
        <div className="drag_content">
          <div
            className={cx('holder bg-source-bg bg-source-bg-c', {
              'bc-window-border': !isDragHover,
              'bc-action': isDragHover,
            })}
          >
            <div className="box centered">
              {!isMobile() && <TabCenterContent isDragHover={isDragHover} />}
              <UploadButton allowedFormat={uploadAllowedFormat} ref={buttonContainerRef} />
            </div>
          </div>
        </div>
      </div>
      <TabFooter />
    </div>
  );
};

const LocalTabWithUpload = getComponentConsumer(LocalTab);

const LocalTabWithDnD = ({ addToUploadQueue }) => (
  <DndProvider backend={FolderAwareHTML5Backend}>
    <LocalTabWithUpload addToUploadQueue={addToUploadQueue} />
  </DndProvider>
);

export default TabPanel({
  id: UPLOAD_SOURCES.LOCAL,
  root: true,
})(LocalTabWithDnD);
